import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import hyperid from 'hyperid';
import ClientCookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Arkose, Nudata, Sardine } from '..';
import { DeviceVendor, type Journey, type RegisterDeviceResult, type VendorClientInput } from '../../.generated/types';
import { useClientData, useDeviceData, useDeviceId } from '../hooks';
import { registerDevice } from '../server/actions';
import { parseUrlDeviceData } from '../server/utils/urlDeviceData';
import { determineApolloClient, determineAuthToken, determineJourney } from '../server/utils/utilities';

const SELECTOR = 'device-registration';

export interface AbstractDeviceRegistrationProps {
	authToken?: string;
	apolloClient?: ApolloClient<NormalizedCacheObject>;
	onRegistrationComplete?: (registrationId: string, vendors: DeviceVendor[]) => void;
	onRegistrationFail?: () => void;
	onVendorsComplete?: (registrationId: string, vendorClientInputs: VendorClientInput[]) => void;
	journey?: Journey;
	deviceIntelligenceSessionKey?: string;
}

export function AbstractDeviceRegistration(props: AbstractDeviceRegistrationProps) {
	const { clientData } = useClientData();
	const { deviceData } = useDeviceData();
	const { env } = useDeviceId();
	const authToken = determineAuthToken(props);
	const client = determineApolloClient(props, authToken);
	const journey = determineJourney(props);
	const { platform } = parseUrlDeviceData();
	const [registerDeviceResult, setRegisterDeviceResult] = useState<RegisterDeviceResult | undefined>();
	const [arkoseSessionId, setArkoseSessionId] = useState<string | undefined>();
	const [nudataSessionId, setNudataSessionId] = useState<string | undefined>();
	const [sardineCompleted, setSardineCompleted] = useState(false);
	const [calledOnRegistrationComplete, setCalledOnRegistrationComplete] = useState(false);
	const [calledOnVendorsComplete, setCalledOnVendorsComplete] = useState(false);

	const onRegistrationComplete = useMemo(() => props.onRegistrationComplete || (() => {}), [props]);
	const onRegistrationFail = useMemo(() => props.onRegistrationFail || (() => {}), [props]);
	const onVendorsComplete = useMemo(() => props.onVendorsComplete || (() => {}), [props]);
	const registrationId: string | undefined = registerDeviceResult && registerDeviceResult.registration_id;
	const vendors: DeviceVendor[] = useMemo(() => {
		return registerDeviceResult ? registerDeviceResult.vendors : [];
	}, [registerDeviceResult]);
	const vendorClientInputs: VendorClientInput[] = vendors
		.map(vendor => {
			if (vendor === DeviceVendor.Arkose && arkoseSessionId) {
				return {
					name: DeviceVendor.Arkose,
					arkose: {
						session_id: arkoseSessionId,
					},
				};
			}

			if (vendor === DeviceVendor.Nudata && nudataSessionId) {
				return {
					name: DeviceVendor.Nudata,
					nudata: {
						session_id: nudataSessionId,
					},
				};
			}

			if (vendor === DeviceVendor.Segment) {
				return {
					name: DeviceVendor.Segment,
					segment: {
						anonymous_id: ClientCookies.get('ajs_anonymous_id') || '',
					},
				};
			}

			if (vendor === DeviceVendor.Sardine && sardineCompleted && props.deviceIntelligenceSessionKey) {
				return {
					name: DeviceVendor.Sardine,
					sardine: {
						session_key: props.deviceIntelligenceSessionKey,
					},
				};
			}

			return { name: DeviceVendor.Invalid };
		})
		.filter(vendorClientInput => vendorClientInput.name !== DeviceVendor.Invalid);

	const shouldRenderArkose: boolean = vendors.includes(DeviceVendor.Arkose);
	const shouldRenderNudata: boolean = vendors.includes(DeviceVendor.Nudata);
	const shouldRenderSardine: boolean = vendors.includes(DeviceVendor.Sardine);

	const onArkoseCompleted = useCallback((sessionId: string) => setArkoseSessionId(sessionId), [setArkoseSessionId]);
	const onNudataCompleted = useCallback((sessionId: string) => setNudataSessionId(sessionId), [setNudataSessionId]);
	const onSardineCompleted = useCallback(() => setSardineCompleted(true), [setSardineCompleted]);

	const shouldRegisterDevice: boolean =
		deviceData.vendor_id !== '' && (!registerDeviceResult || !registerDeviceResult.registration_id);

	useEffect(() => {
		if (shouldRegisterDevice && clientData) {
			registerDevice({ authToken, client, clientData, deviceData, platform, journey })
				.then(result => setRegisterDeviceResult(result))
				.catch(onRegistrationFail);
		}
	}, [authToken, client, clientData, deviceData, platform, shouldRegisterDevice, journey, onRegistrationFail]);

	useEffect(() => {
		if (registrationId && !calledOnRegistrationComplete) {
			setCalledOnRegistrationComplete(true);
			onRegistrationComplete(registrationId, vendors);
		}
	}, [calledOnRegistrationComplete, registrationId, vendors, onRegistrationComplete]);

	const shouldCallOnVendorsComplete: boolean =
		!calledOnVendorsComplete && !!registrationId && vendors.length > 0 && vendorClientInputs.length === vendors.length;

	useEffect(() => {
		if (registrationId && shouldCallOnVendorsComplete) {
			setCalledOnVendorsComplete(true);
			onVendorsComplete(registrationId, vendorClientInputs);
		}
	}, [shouldCallOnVendorsComplete, registrationId, vendors, vendorClientInputs, onVendorsComplete]);

	// Reset state when journey changes
	useEffect(() => {
		setRegisterDeviceResult(undefined);
		setArkoseSessionId(undefined);
		setNudataSessionId(undefined);
		setCalledOnRegistrationComplete(false);
		setCalledOnVendorsComplete(false);
	}, [journey]);

	let arkoseComponent;
	if (shouldRenderArkose) {
		arkoseComponent = <Arkose environment={env} onCompleted={onArkoseCompleted} />;
	}

	let nudataComponent;
	if (shouldRenderNudata) {
		nudataComponent = <Nudata environment={env} onCompleted={onNudataCompleted} />;
	}

	let sardineComponent;
	if (shouldRenderSardine) {
		sardineComponent = (
			<Sardine
				sessionKey={props.deviceIntelligenceSessionKey || hyperid().uuid}
				environment={env}
				journey={journey}
				onCompleted={onSardineCompleted}
			/>
		);
	}

	return (
		<div data-testid={SELECTOR} id={SELECTOR}>
			{arkoseComponent}
			{nudataComponent}
			{sardineComponent}
		</div>
	);
}
